import type { OfficePresetManifest } from "@bezoned/shared/schemas/office/office-preset-manifest.schema.js";
import type { TeamOfficePresetUpdateBody } from "@bezoned/shared/types/api/team/team-office-preset-update.types.js";

import { getTableChannelAssignmentPayload } from "./channel-assignment.helpers.js";
import type { ChannelDraftAssignment } from "./channel-assignment.helpers.js";

type NewPresetTable = OfficePresetManifest["tables"][number];

export const DO_NOT_USE = "__do_not_use__" as const;

export interface CurrentTableDisplayItem {
  msChannelId: string;
  displayName: string;
}

export const buildInitialRowSelections = (
  currentTableCount: number,
  newPresetTables: NewPresetTable[]
): Record<number, string> => {
  const maxLength = Math.max(currentTableCount, newPresetTables.length);
  const selections: Record<number, string> = {};

  for (let i = 0; i < maxLength; i += 1) {
    if (i < newPresetTables.length) {
      selections[i] = newPresetTables.at(i)?.id ?? "";
    } else {
      selections[i] = DO_NOT_USE;
    }
  }

  return selections;
};

export const buildPayload = (
  newPresetTables: NewPresetTable[],
  rowSelections: Record<number, string>,
  currentTableDisplayData: CurrentTableDisplayItem[],
  channelAssignments: Record<string, ChannelDraftAssignment>
): TeamOfficePresetUpdateBody["assignments"] => {
  const tableIdToRowIndex = new Map<string, number>();
  for (const [rowIdx, selectedId] of Object.entries(rowSelections)) {
    if (selectedId) {
      tableIdToRowIndex.set(selectedId, Number(rowIdx));
    }
  }

  return newPresetTables.map((newTable) => {
    const rowIdx = tableIdToRowIndex.get(newTable.id);

    if (rowIdx !== undefined) {
      const currentTable = currentTableDisplayData.at(rowIdx);

      if (currentTable?.msChannelId) {
        return getTableChannelAssignmentPayload(newTable.id, {
          mode: "existing_channel",
          msChannelId: currentTable.msChannelId,
        });
      }
    }

    const channelAssignment = channelAssignments[newTable.id] ?? {
      mode: "create_new",
    };

    return getTableChannelAssignmentPayload(newTable.id, channelAssignment);
  });
};
